import React, { Suspense, useState, useEffect, useMemo } from "react";
import { Await, useLoaderData, useRevalidator, useRouteLoaderData } from "react-router-dom";
import { Button, Col, Table, Container, Form, Row, ButtonGroup, Stack, InputGroup, Card } from "react-bootstrap";
import { axiosInstance } from "../auth/Auth";
import LoadingIndicator from "../components/LoadingIndicator";
import { PencilFill, TrashFill, XLg, CheckLg, Search } from "react-bootstrap-icons";
import ConfirmPrompt from "../components/ConfirmPrompt";
import useSortableData from "../components/useSortableData";
import EmptyTableRow from "../components/EmptyTableRow";
import EmailAddressPicker from "../components/Pickers/EmailAddressPicker";
import StationPicker from "../components/Pickers/StationPicker";
import SpinsPicker from "../components/Pickers/SpinsPicker";
import PageDesciption from "../components/PageDesciption";
import { useUserFormState } from "../components/UserSelection";
import { toast } from "react-toastify";
import RATooltip from "../components/RATooltip";

const MusicAlerts = () => {
    const moduleName = "musicAlerts";
    const [errorCode, setErrorCode] = useState(null);
    const { competitorRadios, userInfo } = useRouteLoaderData("root");
    const { reports } = useLoaderData();
    const [reportsData, setReportsData] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [deleteReportId, setDeleteReportId] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [radioNames, setRadioNames] = useState([]);
    const revalidator = useRevalidator();
    const [formData, setFormData] = useUserFormState(moduleName);

    // Load data to a state
    useEffect(() => {
        async function setData() {
            const radioIds = (await competitorRadios).data.map(x => x.radio_id);
            setReportsData((await reports).data.filter(x => radioIds.includes(x.radio_id)));
        }
        setData();
    }, [competitorRadios, reports]);

    // Preset user's email address
    useEffect(() => {
        async function getEmailAddress() {
            const userInfoData = (await userInfo).data;
            if (userInfoData.email) {
                setFormData(current => ({
                    ...current,
                    email_address: [{ email: userInfoData.email }],
                }));
            }
        }
        getEmailAddress();
    }, [userInfo]);

    useEffect(() => {
        async function getRadioNames() {
            const radiosData = (await competitorRadios).data;
            setRadioNames(
                radiosData.map(x => {
                    return { radio_id: x.radio_id, radio_name: x.radio_name };
                })
            );
        }
        if (competitorRadios) {
            getRadioNames();
        }
    }, [competitorRadios]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        const resolvedUser = (await userInfo).data;
        const input = {
            user_id: resolvedUser.user_id,
            radio_id: formData.station[0].radio_id,
            min_spins: formData.min_spins,
            email_address: formData.email_address?.map(x => x.email).join(","),
            new: formData.new,
        };
        try {
            const response = await axiosInstance.post("reports/music_alert/", input);
            revalidator.revalidate();
            setFormData({ ...formData, email_address: resolvedUser.email ? [{ email: resolvedUser.email }] : [] });
            setErrorCode(null);
            toast.success(`Music Alert has been created.`, { autoClose: 5000 });
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
            toast.error(`Error trying to create a Music Alert.`);
        }
    }

    // Next 2 functions handles edit/delete existing reports
    async function handleSaveClick(report) {
        try {
            const response = await axiosInstance.put("/reports/music_alert/" + report.report_id, {
                ...report,
                email_address: report.email_address.map(x => x.email).join(","),
            });
            revalidator.revalidate();
            setSelectedReport(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    async function deleteMusicAlertReport(id) {
        try {
            const response = await axiosInstance.delete("/reports/music_alert/" + id);
            revalidator.revalidate();
            setDeleteReportId(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    const getRadioName = radio_id => radioNames?.find(x => x.radio_id === radio_id).radio_name;

    const newMusicAlertsMemo = useMemo(() => {
        if (reportsData) {
            let filteredData = reportsData;
            if (searchValue) {
                filteredData = filteredData.filter(
                    x =>
                        getRadioName(x.radio_id).toLowerCase().includes(searchValue.toLowerCase().trim()) ||
                        x.radio_id.toString().includes(searchValue.trim()) ||
                        x.email_address.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
                        x.min_spins?.toString().includes(searchValue.trim())
                );
            }
            return filteredData;
        }
    }, [reportsData, searchValue]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(newMusicAlertsMemo);

    return (
        <Container fluid className="p-4">
            <Row>
                <Col className="bg-white p-4">
                    <h1 className="text-uppercase">Music Alerts</h1>
                    <Form className="row" onSubmit={handleFormSubmit}>
                        <Col xs={12} xl={10}>
                            <Row className="pt-4 gy-3" id="music-alerts ">
                                <StationPicker formData={formData} setFormData={setFormData} name={moduleName} includeCompetitors />
                                <SpinsPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                <Form.Group as={Col} controlId={`type-picker`} className="col-12 col-xl-2 min-w-perspective ">
                                    <Form.Label>
                                        Type
                                        <RATooltip className="ms-2">
                                            <p>
                                                Choose to display only new songs released in the last year, songs older than 12 months there were not previously
                                                played, or both.
                                            </p>
                                        </RATooltip>
                                    </Form.Label>
                                    <Form.Select value={formData.new ?? ""} onChange={e => setFormData({ ...formData, new: e.target.value || null })}>
                                        <option value={"true"}>New Songs</option>
                                        <option value={"false"}>Old songs</option>
                                        <option value={""}>New & Old</option>
                                    </Form.Select>
                                </Form.Group>
                                <EmailAddressPicker formData={formData} setFormData={setFormData} name={moduleName} />
                            </Row>
                        </Col>
                        <Col xs={12} xl={2} className="text-center mt-3 text-xl-end align-self-end ms-auto">
                            <Button
                                type="submit"
                                className="btn-ra-yellow text-uppercase px-5"
                                disabled={!formData.station.length || !formData.email_address.length}>
                                SET UP
                            </Button>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr className="m-2"></hr>
            <Row>
                <Col className="bg-white p-4">
                    {!reportsData?.length > 0 && !errorCode && (
                        <PageDesciption
                            title={"Music Alerts"}
                            whatList={[
                                "Get an automated alert after a new song is played on a station “X” amount of times",
                                "Choose any station in your portfolio, set a minimum number of spins, and enter one or more emails",
                                "This is an easy way to keep an eye on new adds in your market or on a station you look to for inspiriation without checking manually",
                            ]}
                            knowList={[
                                { text: "Add your entire music department emails so everyone stays in the loop" },
                                {
                                    text: "A good minimum count starts at at least 5-6 spins, this helps avoid single plays of songs on a chart show or a call in spin",
                                },
                                { text: "If you no longer need the info, just hit the red trash can on the alert to delete it" },
                            ]}
                            relatedLinks={[
                                {
                                    title: "Observation Made Easy: New Music Alerts",
                                    url: "https://radioanalyzer.com/blog/observation-made-easy-new-music-alerts",
                                },
                            ]}
                            videoSrc="https://www.youtube.com/embed/c2vUM5iqtkU?si=Qg1s97vOcC7GEiGb"
                        />
                    )}
                    {!reportsData ? (
                        <span colSpan={12}>
                            <LoadingIndicator />
                        </span>
                    ) : (
                        reportsData.length > 0 && (
                            <>
                                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                                    <p>Here you'll find an overview of your existing music alerts. Add new alerts on the top.</p>
                                </Stack>
                                <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
                                    <Col xs={12} xl={8}>
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={searchValue}
                                                onChange={e => {
                                                    setSearchValue(e.target.value);
                                                }}
                                                placeholder="search by radio name, spins or email address"
                                            />
                                        </InputGroup>
                                    </Col>
                                </Stack>

                                <div className="row row-cols-1 row-cols-sm- row-cols-md-2 row-cols-lg-2 row-cols-xl-4 row-cols-xxl-4">
                                    {sortedItems.length ? (
                                        sortedItems.map((report, index) => (
                                            <div key={index} className="col mb-4">
                                                <Card className="w-100 ">
                                                    <Suspense>
                                                        <Await resolve={competitorRadios}>
                                                            {resolvedRadios => (
                                                                <Card.Header className="bg-ra-dark-blue text-light d-flex justify-content-between align-items-center text-center fw-bold">
                                                                    <div className="d-flex justify-content-center align-items-center fw-bold fs-6 text flex-grow-1">
                                                                        {
                                                                            resolvedRadios?.data?.filter(radio => radio.radio_id === report.radio_id)[0]
                                                                                ?.radio_name
                                                                        }
                                                                    </div>
                                                                    {selectedReport?.report_id === report.report_id ? (
                                                                        <ButtonGroup size="sm" className="pe-1">
                                                                            <Button
                                                                                variant="success"
                                                                                title="Save"
                                                                                disabled={!selectedReport?.email_address.length}
                                                                                onClick={() => handleSaveClick(selectedReport)}>
                                                                                <CheckLg />
                                                                            </Button>
                                                                            <Button variant="danger" title="Discard" onClick={() => setSelectedReport(null)}>
                                                                                <XLg />
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    ) : (
                                                                        <ButtonGroup size="sm" className="pe-1">
                                                                            <Button
                                                                                variant="primary"
                                                                                title={"Edit"}
                                                                                onClick={() =>
                                                                                    setSelectedReport({
                                                                                        ...report,
                                                                                        email_address: report.email_address.split(",").map(x => ({ email: x })),
                                                                                    })
                                                                                }>
                                                                                <PencilFill />
                                                                            </Button>
                                                                            <Button
                                                                                variant="danger"
                                                                                title={"Delete"}
                                                                                onClick={() => setDeleteReportId(report.report_id)}>
                                                                                <TrashFill />
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    )}
                                                                </Card.Header>
                                                            )}
                                                        </Await>
                                                    </Suspense>
                                                    <Card.Body>
                                                        <Card.Text className="text-start ">
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <Row className="gy-2">
                                                                    <SpinsPicker
                                                                        formData={selectedReport}
                                                                        setFormData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                        inputOnly={false}
                                                                    />
                                                                    <Form.Group as={Col} controlId={`type-picker-edit`}>
                                                                        <Form.Label className=" text-nowrap">
                                                                            Type
                                                                            <RATooltip className="ms-2">
                                                                                <p>
                                                                                    Choose to display only new songs released in the last year, songs older than
                                                                                    12 months there were not previously played, or both.
                                                                                </p>
                                                                            </RATooltip>
                                                                        </Form.Label>
                                                                        <Form.Select
                                                                            value={selectedReport?.new ?? ""}
                                                                            onChange={e =>
                                                                                setSelectedReport({
                                                                                    ...selectedReport,
                                                                                    new: e.target.value || null,
                                                                                })
                                                                            }>
                                                                            <option value="true">New Songs</option>
                                                                            <option value="false">Old songs</option>
                                                                            <option value="">New & Old</option>
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                </Row>
                                                            ) : (
                                                                <div className="text-center">
                                                                    <div>
                                                                        <span>
                                                                            <strong>{report.min_spins} spins</strong>
                                                                        </span>
                                                                        <div className="vr d-xl-inline-block mx-2"></div>

                                                                        <span>
                                                                            <strong>
                                                                                {report.new && "New Songs"}
                                                                                {report.new === false && "Old songs"}
                                                                                {report.new === null && "New & Old"}
                                                                            </strong>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Card.Text>
                                                        <Card.Text className="music-alerts-email-card-edit text-center">
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <EmailAddressPicker
                                                                    formData={selectedReport}
                                                                    setFormData={setSelectedReport}
                                                                    name={moduleName + "edit"}
                                                                    inputOnly={true}
                                                                />
                                                            ) : (
                                                                <span className="rbt-token p-2">{report.email_address?.split(",").join(", ")}</span>
                                                            )}
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="row justify-content-center">
                                            <div className="col-12">
                                                <span>No results.</span>
                                            </div>
                                        </div>
                                    )}
                                    <ConfirmPrompt
                                        show={deleteReportId !== null}
                                        handleClose={() => setDeleteReportId(null)}
                                        handleConfirm={async () => {
                                            await deleteMusicAlertReport(deleteReportId);
                                            setDeleteReportId(null);
                                        }}
                                    />
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default MusicAlerts;
